<template>
  <Head title="Crear Cuenta - Realgestión" />
  <div class="min-h-screen bg-slate-50 flex items-center justify-center p-4 sm:p-6 lg:p-8">
    <div class="w-full max-w-4xl bg-white rounded-2xl shadow-lg border border-gray-200 grid lg:grid-cols-12 overflow-hidden">
      <!-- Panel lateral izquierdo de beneficios (Luminoso, limpio y con logo Realgestión) -->
      <div class="hidden lg:flex lg:col-span-5 bg-gradient-to-br from-slate-50 via-orange-50/30 to-blue-50/20 p-8 xl:p-10 border-r border-gray-200 flex-col justify-between">
        <div>
          <!-- Logo de Realgestión -->
          <div class="mb-6">
            <logo class="h-9 w-auto max-w-[170px]" />
          </div>

          <!-- Badge -->
          <div class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-orange-100 border border-orange-200 text-orange-800 text-xs font-semibold mb-4">
            <i class="fas fa-sparkles text-orange-600" />
            <span>Plan Gratuito</span>
          </div>

          <!-- Título y subtítulo conciso -->
          <h2 class="text-2xl font-extrabold text-gray-900 tracking-tight leading-snug mb-2">
            Gestiona tu negocio de forma simple
          </h2>
          <p class="text-gray-600 text-sm leading-relaxed mb-6">
            Comienza gratis con lo esencial y escala a Pro cuando tu empresa crezca.
          </p>

          <!-- Lista de beneficios puntual -->
          <ul class="space-y-3.5">
            <li class="flex items-center gap-3 text-sm text-gray-800 font-medium">
              <span class="w-6 h-6 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center text-xs flex-shrink-0">
                <i class="fas fa-check" />
              </span>
              <span>Inventario y stock en tiempo real</span>
            </li>

            <li class="flex items-center gap-3 text-sm text-gray-800 font-medium">
              <span class="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs flex-shrink-0">
                <i class="fas fa-check" />
              </span>
              <span>Ventas, cotizaciones y cobros ágiles</span>
            </li>

            <li class="flex items-center gap-3 text-sm text-gray-800 font-medium">
              <span class="w-6 h-6 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xs flex-shrink-0">
                <i class="fas fa-check" />
              </span>
              <span>Catálogo digital listo para compartir</span>
            </li>
          </ul>
        </div>

        <!-- Pie discreto -->
        <div class="pt-6 border-t border-gray-200 text-xs text-gray-500 font-medium">
          ✓ Sin tarjeta de crédito &bull; ✓ Pásate a Pro cuando quieras
        </div>
      </div>

      <!-- Panel derecho: Formulario de registro -->
      <div class="lg:col-span-7 p-6 sm:p-8 lg:p-10 flex flex-col justify-center bg-white">
        <!-- Logo en versión móvil -->
        <div class="lg:hidden mb-5 text-center">
          <logo class="h-8 w-auto max-w-[150px] mx-auto mb-1" />
        </div>

        <div class="max-w-sm sm:max-w-md mx-auto w-full">
          <!-- Encabezado -->
          <div class="mb-5 text-center lg:text-left">
            <h1 class="text-2xl font-extrabold text-gray-900 tracking-tight">
              Crear nueva cuenta
            </h1>
            <p class="text-xs sm:text-sm text-gray-500 mt-1">
              Regístrate gratis para comenzar
            </p>
          </div>

          <!-- Alerta de Error -->
          <div v-if="displayError" class="mb-4 bg-red-50 border-l-4 border-red-500 p-3 rounded-r text-xs sm:text-sm text-red-700">
            {{ displayError }}
          </div>

          <!-- Botón de Registro con Google -->
          <div class="mb-4">
            <a
              href="/auth/google/redirect"
              class="w-full flex items-center justify-center gap-3 py-2.5 px-4 bg-white hover:bg-gray-50 text-gray-700 font-medium text-sm border border-gray-300 rounded-lg shadow-sm hover:border-gray-400 transition-all duration-150"
            >
              <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              <span>Continuar con Google</span>
            </a>
          </div>

          <!-- Separador -->
          <div class="relative my-4">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-200" />
            </div>
            <div class="relative flex justify-center text-xs uppercase">
              <span class="bg-white px-2.5 text-gray-400 font-medium">o con tu correo</span>
            </div>
          </div>

          <!-- Formulario -->
          <form @submit.prevent="submit">
            <div class="space-y-3">
              <div class="grid grid-cols-2 gap-3">
                <text-input
                  v-model="form.first_name"
                  :error="form.errors.first_name"
                  label="Nombre *"
                  placeholder="Juan"
                  class="text-sm"
                  required
                />
                <text-input
                  v-model="form.last_name"
                  :error="form.errors.last_name"
                  label="Apellido *"
                  placeholder="Pérez"
                  class="text-sm"
                  required
                />
              </div>

              <text-input
                v-model="form.email"
                :error="form.errors.email"
                type="email"
                label="Email *"
                placeholder="juan@correo.com"
                class="text-sm"
                required
              />

              <phone-input
                v-model="form.phone"
                :error="form.errors.phone"
                label="Teléfono *"
                class="text-sm"
                required
              />

              <div class="grid grid-cols-2 gap-3">
                <password-input
                  v-model="form.password"
                  :error="form.errors.password"
                  label="Contraseña *"
                  class="text-sm"
                  required
                />
                <password-input
                  v-model="form.password_confirmation"
                  :error="form.errors.password_confirmation"
                  label="Confirmar *"
                  class="text-sm"
                  required
                />
              </div>
            </div>

            <!-- Botón de Envío -->
            <div class="mt-5">
              <loading-button
                :loading="form.processing"
                class="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold py-2.5 px-4 rounded-lg shadow hover:shadow-md transition-all duration-150 text-sm tracking-wide flex justify-center items-center"
                type="submit"
              >
                Crear cuenta gratis
              </loading-button>
            </div>

            <!-- Enlace a Login y Términos discretos -->
            <div class="mt-4 text-center space-y-1.5">
              <p class="text-xs text-gray-600">
                ¿Ya tienes cuenta?
                <Link href="/login" class="text-orange-600 hover:text-orange-700 font-medium ml-1">
                  Inicia sesión
                </Link>
              </p>
              <p class="text-gray-400" style="font-size: 11px;">
                Al registrarte aceptas nuestros Términos y Condiciones.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Logo from '@/Shared/Logo'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'
import PhoneInput from '@/Shared/PhoneInput'
import PasswordInput from '@/Shared/PasswordInput'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    Logo,
    TextInput,
    PhoneInput,
    PasswordInput,
  },
  data() {
    return {
      form: this.$inertia.form({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        password_confirmation: '',
      }),
    }
  },
  computed: {
    displayError() {
      return (
        this.form.errors.register_error ||
        this.$page.props.errors?.register_error ||
        this.$page.props.flash?.error ||
        null
      )
    },
  },
  methods: {
    submit() {
      this.form.post('/register', {
        onSuccess: () => {},
      })
    },
  },
}
</script>